/**
 * GLOBAL CANDLE CACHE — TradingView-grade data service
 *
 * Architecture:
 *  1. Module-level Map persists across SPA navigations
 *  2. Initial load fetches latest 300 candles (200 visible + buffer)
 *  3. get() returns cached data INSTANTLY, triggers background refresh
 *  4. Prefetch on pan — when user scrolls near edge, loads older candles
 *  5. WebSocket live stream for real-time candle updates
 *  6. Subscribers notified on ANY cache update (chart re-renders seamlessly)
 *
 * The user never sees a loading spinner after first load.
 */

import { CANDLE_CONSTRUCTOR_URL, MARKET_INGESTION_URL } from '@/utils/constants';

export interface CachedCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
  tick_volume?: number;
  /** candle-constructor's verdict: false = the bar is still forming (its period has not ended). */
  is_closed?: boolean;
}

interface CacheEntry {
  data: CachedCandle[];
  timestamp: number;
  fetching: boolean;
  /** Earliest candle time we have (for paginating backwards) */
  oldestTime: number;
  /** Whether we've exhausted history (server returned fewer than requested) */
  historyExhausted: boolean;
}

type CacheListener = (key: string, data: CachedCandle[]) => void;

// ── Singleton state ─────────────────────────────────────────────────
const cache = new Map<string, CacheEntry>();
const listeners = new Set<CacheListener>();
const CACHE_TTL = 5 * 60_000;
// 90s, not 30s: the background loop re-fetches EVERY cached timeframe at 5000 rows
// each — 13 ClickHouse queries per cycle. At 30s that kept the candle-constructor
// pinned (~36% CPU on a 4-core box). 90s is plenty for historical/replay data and
// gives the chart + the rest of the system the CPU back.
const BACKGROUND_REFRESH = 90_000;
const DEFAULT_ASSET = '1HZ25V';
const DEFAULT_INITIAL_COUNT = 5000;      // load all available history (API max)
const PREFETCH_BATCH_SIZE = 500;         // candles per prefetch batch
const PRELOAD_TIMEFRAMES = ['1s', '2s', '1m', '2m', '3m', '5m', '15m', '30m', '1h', '4h', '1D', '1W', '1Mo'];

// Map candle symbol → Deriv tick symbol for live subscription
const CANDLE_TO_TICK: Record<string, string> = {
  '1HZ25V': '1HZ25V',
  'R_25': 'R_25',
  'CRASH600': 'CRASH600',
  'frxXAUUSD': 'frxXAUUSD',
  'STEP_INDEX': 'STPIDX',
  'BOOM1000': 'BOOM1000',
  'BOOM500': 'BOOM500',
  'CRASH1000': 'CRASH1000',
  'CRASH300': 'CRASH300',
  'R_10': 'R_10',
  'R_50': 'R_50',
  'R_75': 'R_75',
  'R_100': 'R_100',
  '1HZ10V': '1HZ10V',
  '1HZ50V': '1HZ50V',
  '1HZ75V': '1HZ75V',
  '1HZ100V': '1HZ100V',
};

/**
 * Client-side aggregation map.
 * Several timeframes have synthetic/uniform data in the DB (1m, 5m, 1h, 4h)
 * or very old data (2m: Jun 2020). These are derived from richer real-data TFs
 * and aggregated on the client.
 *   2m  ← aggregate 1s ticks into 2-minute buckets  (Dec 2023 vs Jun 2020)
 *   1m  ← aggregate 1s ticks into 1-minute buckets  (Dec 2023)
 *   5m  ← aggregate 3m bars into 5-minute buckets   (Jan 2022)
 *   1h  ← aggregate 30m bars into 60-minute buckets (Oct 2025 — latest real data)
 *   4h  ← aggregate 30m bars into 240-minute buckets(Oct 2025 — latest real data)
 */
// NO client-side aggregation. The candle-constructor (ClickHouse) serves EVERY
// timeframe natively (verified: 1s,2s,1m,2m,3m,5m,15m,30m,1h,4h,1D,1W,1Mo) with
// real distinct bars. The old map "aggregated" fine TFs (1m,2m,3m,5m,15m) from a
// COARSER 30m source — which is impossible (you cannot build 1-minute bars from
// 30-minute bars) — so 1m never showed real 1m and several TFs rendered nothing.
// Every timeframe now fetches its OWN real data directly. The backend is the single
// source of truth; if a TF isn't stored, the backend aggregates it server-side.
const AGGREGATE_FROM: Record<string, { source: string; intervalMs: number }> = {};

// Source TF milliseconds per bar (used for count calculation)
const SOURCE_TF_MS: Record<string, number> = {
  '1s':    1_000,
  '30m': 1_800_000,
};

let preloadStarted = false;
let backgroundTimer: ReturnType<typeof setInterval> | null = null;

// ── Circuit breaker ─────────────────────────────────────────────────
// candle-constructor lives on a contended 4-core box. When it saturates,
// every fetch times out; if the cache keeps firing new requests the hung
// connections pile up through the Vite proxy (we hit 2045 once) which
// keeps the service pinned — a self-reinforcing storm. This breaker trips
// after a run of failures and holds requests off until a cooldown, so the
// service can drain and recover instead of being hammered while down.
const CB_TRIP_THRESHOLD = 4;       // consecutive failures before tripping
const CB_COOLDOWN_MS = 20_000;     // hold-off once tripped
// PER-KEY, NOT GLOBAL. A single global breaker meant four slow requests on ANY
// timeframe blanked EVERY timeframe for 20s — a slow 1h fetch took down the 15m chart
// the user was actually watching. Observed live: 19 candle requests succeeded while 30m
// and 1h timed out, and the chart still went blank. Failure on one series says nothing
// about the others, so each key trips independently.
const _cbFailures = new Map<string, number>();
const _cbOpenUntil = new Map<string, number>();
function circuitOpen(key = '*'): boolean {
  return Date.now() < (_cbOpenUntil.get(key) ?? 0);
}
function recordFetchOk(key = '*'): void {
  _cbFailures.delete(key);
  _cbOpenUntil.delete(key);
}
function recordFetchFail(key = '*'): void {
  const n = (_cbFailures.get(key) ?? 0) + 1;
  if (n >= CB_TRIP_THRESHOLD) {
    _cbOpenUntil.set(key, Date.now() + CB_COOLDOWN_MS);
    _cbFailures.set(key, 0); // re-arm; if it's still down after cooldown it trips again
    console.warn(`candleCache: circuit OPEN for ${CB_COOLDOWN_MS / 1000}s on ${key} — backing off THAT series only`);
  } else {
    _cbFailures.set(key, n);
  }
}

// ── WebSocket live stream ───────────────────────────────────────────
let liveWs: WebSocket | null = null;
let liveWsReconnectTimer: ReturnType<typeof setTimeout> | null = null;

// ── Key helper ──────────────────────────────────────────────────────
function cacheKey(asset: string, tf: string) {
  return `${asset}_${tf}`;
}

// ── WebSocket URL from HTTP URL ─────────────────────────────────────
function wsUrl(): string {
  const http = CANDLE_CONSTRUCTOR_URL;
  // Relative path (e.g. /svc/candles) — build from current host
  if (http.startsWith('/')) {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}${http}/ws/candles`;
  }
  // Absolute URL — swap http→ws
  const ws = http.replace(/^http/, 'ws');
  return `${ws}/ws/candles`;
}

// ── Core fetch (initial load — latest N candles) ────────────────────
async function fetchLatest(asset: string, tf: string, count = DEFAULT_INITIAL_COUNT): Promise<CachedCandle[]> {
  // ── Client-side aggregation for TFs with poor/synthetic DB data ──
  const agg = AGGREGATE_FROM[tf];
  if (agg) {
    const srcIntervalMs = SOURCE_TF_MS[agg.source] ?? 60_000;
    // Cap at API max (5000) to avoid 422 errors
    const srcCount = Math.min(5000, Math.ceil(count * (agg.intervalMs / srcIntervalMs) * 1.3));
    const sourceData = await fetchLatest(asset, agg.source, srcCount);
    const aggregated = aggregateCandles(sourceData, agg.intervalMs);
    const key = cacheKey(asset, tf);
    const oldest = aggregated[0]?.time ?? 0;
    cache.set(key, {
      data: aggregated,
      timestamp: Date.now(),
      fetching: false,
      oldestTime: oldest,
      historyExhausted: false,
    });
    notifyListeners(key, aggregated);
    return aggregated;
  }

  const key = cacheKey(asset, tf);
  const entry = cache.get(key);
  if (entry?.fetching) return entry.data;
  // Breaker open for THIS series → serve what we have; other timeframes are unaffected.
  if (circuitOpen(key)) return entry?.data ?? [];
  if (entry) entry.fetching = true;

  try {
    const url = `${CANDLE_CONSTRUCTOR_URL}/api/v2/candles/${asset}/latest?timeframe=${tf}&count=${count}`;
    // 12s tripped constantly while the box sat at load 17 and the server itself
    // answered in 0.4s — the timeout, not the service, was blanking the chart.
    const res = await fetch(url, { signal: AbortSignal.timeout(30000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    const raw = json.candles || json;
    const data = normalizeCandles(raw);

    const oldest = data.length > 0 ? data[0].time : 0;
    cache.set(key, {
      data,
      timestamp: Date.now(),
      fetching: false,
      oldestTime: oldest,
      historyExhausted: data.length < count,
    });

    recordFetchOk(key);
    notifyListeners(key, data);
    return data;
  } catch (e) {
    if (entry) entry.fetching = false;
    recordFetchFail(key);
    console.warn(`candleCache: fetchLatest failed ${key}`, e);
    return entry?.data ?? [];
  }
}

// ── Fetch older candles (paginate backwards for infinite scroll) ─────
async function fetchOlder(asset: string, tf: string, batchSize = PREFETCH_BATCH_SIZE): Promise<CachedCandle[]> {
  const key = cacheKey(asset, tf);
  const entry = cache.get(key);
  if (!entry || entry.fetching || entry.historyExhausted) return entry?.data ?? [];

  // For aggregated timeframes: load older source candles and re-aggregate
  const agg = AGGREGATE_FROM[tf];
  if (agg) {
    const srcIntervalMs = SOURCE_TF_MS[agg.source] ?? 60_000;
    const srcBatch = Math.min(5000, Math.ceil(batchSize * (agg.intervalMs / srcIntervalMs) * 1.3));
    await fetchOlder(asset, agg.source, srcBatch);
    const srcEntry = cache.get(cacheKey(asset, agg.source));
    if (!srcEntry) return entry.data;
    const allAgg = aggregateCandles(srcEntry.data, agg.intervalMs);
    entry.data = allAgg;
    entry.oldestTime = allAgg[0]?.time ?? entry.oldestTime;
    entry.timestamp = Date.now();
    entry.historyExhausted = srcEntry.historyExhausted;
    notifyListeners(key, allAgg);
    return allAgg;
  }

  entry.fetching = true;

  try {
    // Convert oldest time (ms) to ISO for the 'end' parameter.
    // order=DESC is critical: with end=<oldest> the DB applies LIMIT *after*
    // ORDER BY, so asc would return the EARLIEST N candles in all history
    // (e.g. 2020) instead of the N just before the viewport — making scroll-back
    // silently load off-screen data ("history not printing"). desc returns the
    // candles immediately before `end`; the merge below re-sorts ascending.
    const endIso = new Date((entry.oldestTime * 1000) - 1).toISOString();
    const url = `${CANDLE_CONSTRUCTOR_URL}/api/v2/candles/${asset}?timeframe=${tf}&limit=${batchSize}&end=${endIso}&order=desc`;
    const res = await fetch(url, { signal: AbortSignal.timeout(15000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    const raw = json.candles || json;
    const olderCandles = normalizeCandles(raw);

    if (olderCandles.length === 0) {
      entry.historyExhausted = true;
      entry.fetching = false;
      return entry.data;
    }

    // Merge: older + existing (de-duped by time)
    const existingTimes = new Set(entry.data.map(c => c.time));
    const newCandles = olderCandles.filter(c => !existingTimes.has(c.time));
    const merged = [...newCandles, ...entry.data].sort((a, b) => a.time - b.time);

    entry.data = merged;
    entry.oldestTime = merged[0].time;
    entry.fetching = false;
    entry.timestamp = Date.now();
    entry.historyExhausted = olderCandles.length < batchSize;

    notifyListeners(key, merged);
    return merged;
  } catch (e) {
    entry.fetching = false;
    console.warn(`candleCache: fetchOlder failed ${key}`, e);
    return entry.data;
  }
}

// ── Legacy full fetch (for backward compatibility) ──────────────────
async function fetchAndCache(asset: string, tf: string, limit = 10000): Promise<CachedCandle[]> {
  const key = cacheKey(asset, tf);
  const entry = cache.get(key);
  if (entry?.fetching) return entry.data;
  if (entry) entry.fetching = true;

  try {
    const url = `${CANDLE_CONSTRUCTOR_URL}/api/v2/candles/${asset}?timeframe=${tf}&limit=${limit}&order=asc`;
    const res = await fetch(url, { signal: AbortSignal.timeout(12000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    const raw = json.candles || json;
    const data = normalizeCandles(raw);
    const oldest = data.length > 0 ? data[0].time : 0;

    cache.set(key, {
      data,
      timestamp: Date.now(),
      fetching: false,
      oldestTime: oldest,
      historyExhausted: data.length < limit,
    });

    notifyListeners(key, data);
    return data;
  } catch (e) {
    if (entry) entry.fetching = false;
    console.warn(`candleCache: fetch failed ${key}`, e);
    return entry?.data ?? [];
  }
}

// ── Aggregate lower-TF candles into larger time buckets ─────────────
function aggregateCandles(candles: CachedCandle[], intervalMs: number): CachedCandle[] {
  if (candles.length === 0) return [];
  const buckets = new Map<number, CachedCandle[]>();
  for (const c of candles) {
    const tMs = c.time * 1000;
    const bucketMs = Math.floor(tMs / intervalMs) * intervalMs;
    const arr = buckets.get(bucketMs);
    if (arr) arr.push(c);
    else buckets.set(bucketMs, [c]);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([tMs, cs]) => ({
      time: Math.floor(tMs / 1000),
      open:   cs[0].open,
      high:   Math.max(...cs.map(c => c.high)),
      low:    Math.min(...cs.map(c => c.low)),
      close:  cs[cs.length - 1].close,
      volume: cs.reduce((s, c) => s + (c.volume ?? 0), 0),
    }));
}

// ── Normalize raw API response to CachedCandle[] ────────────────────
// is_closed is kept when the server sends it (candle-constructor marks every candle);
// dropping it left the chart unable to tell the forming bar from a closed one.
export function normalizeCandles(raw: any[]): CachedCandle[] {
  return (Array.isArray(raw) ? raw : []).map((c: any) => {
    const out: CachedCandle = {
      time: c.time > 1e12 ? Math.floor(c.time / 1000) : c.time,
      open: +c.open,
      high: +c.high,
      low: +c.low,
      close: +c.close,
      volume: c.volume,
      tick_volume: c.tick_volume,
    };
    if (typeof c.is_closed === 'boolean') out.is_closed = c.is_closed;
    return out;
  });
}

/**
 * Is this bar still forming? The server's is_closed wins; without it, a bar whose period
 * (time + tfSec) has not ended yet is forming.
 */
export function isFormingBar(c: { time: number; is_closed?: boolean }, tfSec: number, nowSec = Date.now() / 1000): boolean {
  if (typeof c.is_closed === 'boolean') return !c.is_closed;
  return c.time + tfSec > nowSec;
}

// ── Notify listeners ────────────────────────────────────────────────
function notifyListeners(key: string, data: CachedCandle[]) {
  for (const fn of listeners) {
    try { fn(key, data); } catch (_) {}
  }
}

// ── Public API ──────────────────────────────────────────────────────

/**
 * Get candles INSTANTLY. Returns cached data synchronously.
 * If cache is empty, triggers initial fetch (latest 300).
 * If cache is stale, triggers background refresh.
 */
export function getCandlesInstant(asset: string, tf: string, limit = DEFAULT_INITIAL_COUNT): CachedCandle[] {
  const key = cacheKey(asset, tf);
  const entry = cache.get(key);

  if (entry) {
    if (Date.now() - entry.timestamp > CACHE_TTL && !entry.fetching) {
      fetchLatest(asset, tf, limit);
    }
    return entry.data;
  }

  // No cache — fire initial fetch, return empty for now
  fetchLatest(asset, tf, limit);
  return [];
}

/**
 * Force fresh fetch and return the data.
 */
export async function getCandlesFresh(asset: string, tf: string, limit = 10000): Promise<CachedCandle[]> {
  return fetchAndCache(asset, tf, limit);
}

/**
 * Load older candles for infinite scroll. Called when user pans near left edge.
 * Returns merged (older + existing) candle array.
 */
export async function loadOlderCandles(asset: string, tf: string, batchSize = PREFETCH_BATCH_SIZE): Promise<CachedCandle[]> {
  return fetchOlder(asset, tf, batchSize);
}

/**
 * Check if more history is available for a given asset/timeframe.
 */
export function hasMoreHistory(asset: string, tf: string): boolean {
  const entry = cache.get(cacheKey(asset, tf));
  return entry ? !entry.historyExhausted : true;
}

/**
 * Subscribe to cache updates.
 */
export function subscribe(fn: CacheListener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Preload latest candles for common timeframes.
 */
// High → low timeframe order. The chart's usual view (higher TFs) loads first and
// FAST; the heavy tick-level frames (1s/2s = millions of rows) come LAST.
const _PRELOAD_ORDER = ['1Mo', '1W', '1D', '4h', '1h', '30m', '15m', '5m', '3m', '2m', '1m', '2s', '1s'];
const _tfRank = (tf: string) => { const i = _PRELOAD_ORDER.indexOf(tf); return i < 0 ? 999 : i; };

export async function preloadAll(asset = DEFAULT_ASSET, timeframes = PRELOAD_TIMEFRAMES) {
  const t0 = performance.now();
  // SEQUENTIAL, not all-at-once. Firing 13 fetches concurrently (incl. tick data)
  // saturated the candle-constructor and tripped the circuit breaker → blank chart.
  // One at a time, high-TF first, keeps each request fast and the chart usable.
  const ordered = [...timeframes].sort((a, b) => _tfRank(a) - _tfRank(b));
  for (const tf of ordered) {
    const key = cacheKey(asset, tf);
    if (cache.has(key)) continue;
    try { await fetchLatest(asset, tf); } catch { /* one TF failing must not stop the rest */ }
  }
  console.log(`candleCache: preloaded ${timeframes.length} timeframes for ${asset} in ${(performance.now() - t0).toFixed(0)}ms`);
}

/** Currently active asset for background refresh */
let _activeRefreshAsset = DEFAULT_ASSET;

/**
 * Switch the active asset — clears stale background refresh and preloads new asset.
 */
export function switchAsset(newAsset: string, timeframes = PRELOAD_TIMEFRAMES) {
  _activeRefreshAsset = newAsset;
  preloadAll(newAsset, timeframes);
  // Trigger background gap-fill from Deriv API for the new asset
  _triggerBackfillIfNeeded(newAsset);
  // Subscribe to live tick stream for new asset (no restart needed)
  const tickSym = CANDLE_TO_TICK[newAsset] ?? newAsset;
  fetch(`${MARKET_INGESTION_URL}/stream/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ symbol: tickSym }),
  }).catch(() => {});
}

/**
 * Start background refresh cycle.
 */
export function startBackgroundRefresh(asset = DEFAULT_ASSET) {
  _activeRefreshAsset = asset;
  if (backgroundTimer) return;

  backgroundTimer = setInterval(() => {
    if (circuitOpen()) return; // don't refresh into a saturated service
    if (typeof document !== 'undefined' && document.hidden) return; // nobody is looking
    const activeAsset = _activeRefreshAsset;
    for (const [key, entry] of cache.entries()) {
      const prefix = activeAsset + '_';
      if (key.startsWith(prefix) && !entry.fetching) {
        const tf = key.slice(prefix.length);
        const age = Date.now() - entry.timestamp;
        if (age > BACKGROUND_REFRESH) {
          fetchLatest(activeAsset, tf);
        }
      }
    }
  }, BACKGROUND_REFRESH);
}

export function stopBackgroundRefresh() {
  if (backgroundTimer) {
    clearInterval(backgroundTimer);
    backgroundTimer = null;
  }
}

// ── Backfill (gap-fill from Deriv REST API via candle-constructor) ──────────

/** Track which assets have an active backfill to avoid duplicate jobs */
const _backfillActive = new Set<string>();

/**
 * Trigger a background gap-fill for an asset if its data is stale.
 * Fire-and-forget: returns immediately. Polls status and refreshes cache on completion.
 */
async function _triggerBackfillIfNeeded(asset: string): Promise<void> {
  if (_backfillActive.has(asset)) return;

  // Check if we have recent data — only backfill if last candle > 2 hours ago
  const entry30m = cache.get(cacheKey(asset, '30m'));
  const lastTime = entry30m?.data?.[entry30m.data.length - 1]?.time ?? 0;
  const twoHoursAgo = Math.floor(Date.now() / 1000) - 2 * 3600;
  if (lastTime > twoHoursAgo) return; // data is fresh enough

  _backfillActive.add(asset);
  try {
    const res = await fetch(
      `${CANDLE_CONSTRUCTOR_URL}/api/v2/backfill/${asset}?all_timeframes=true`,
      { method: 'POST', signal: AbortSignal.timeout(8000) },
    );
    if (!res.ok) return;
    const json = await res.json();
    const jobIds: string[] = json.job_ids ?? [];
    if (jobIds.length > 0) {
      _pollBackfillUntilComplete(asset, jobIds);
    }
  } catch (_) {
    // Network error or candle-constructor unavailable — silently ignore
  } finally {
    // Release lock after poll setup (poll runs independently)
    setTimeout(() => _backfillActive.delete(asset), 300_000); // re-allow after 5 min
  }
}

/**
 * Poll backfill job status; when complete, refresh the 30m cache and
 * re-derive all client-aggregated timeframes (1h, 4h, etc.).
 */
async function _pollBackfillUntilComplete(asset: string, jobIds: string[]): Promise<void> {
  for (let i = 0; i < 90; i++) {        // poll up to 3 minutes
    await new Promise(r => setTimeout(r, 2000));
    try {
      const res = await fetch(
        `${CANDLE_CONSTRUCTOR_URL}/api/v2/backfill/${asset}/status`,
        { signal: AbortSignal.timeout(5000) },
      );
      if (!res.ok) continue;
      const json = await res.json();
      const jobs = ((json.jobs as any[]) ?? []).filter((j) => jobIds.includes(j.job_id));
      const allDone = jobs.every((j) => j.status === 'complete' || j.status === 'error');
      const anyInserted = jobs.some((j) => (j.bars_inserted ?? 0) > 0);

      if (anyInserted) {
        // New historical data available — force full cache refresh for this asset
        console.log(`[candleCache] Backfill inserted data for ${asset} — refreshing cache`);
        for (const tf of PRELOAD_TIMEFRAMES) {
          cache.delete(cacheKey(asset, tf));
        }
        preloadAll(asset, PRELOAD_TIMEFRAMES);
      }

      if (allDone) break;
    } catch (_) {}
  }
}

// ── HTTP Live Polling (replaces WebSocket which candle-constructor doesn't support) ──

let livePollActive = false;
let livePollTimer: ReturnType<typeof setTimeout> | null = null;
let livePollTimerTick: ReturnType<typeof setTimeout> | null = null; // real-time tick-based candles

/**
 * Connect live candle stream.
 * - For 1s/2s timeframes: uses WebSocket for raw heartbeat (zero extra latency).
 * - For all other timeframes: polls /live every 500ms + /latest every 30s.
 * Returns cleanup function.
 */
export function connectLiveStream(asset: string, tf: string): () => void {
  disconnectLiveStream();
  livePollActive = true;

  const agg = AGGREGATE_FROM[tf];

  // ── WebSocket path for ultra-low-latency timeframes ──────────────────
  const useLiveWs = tf === '1s' || tf === '2s';
  if (useLiveWs) {
    const wsEndpoint = wsUrl();
    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (!livePollActive) return;
      ws = new WebSocket(wsEndpoint);
      liveWs = ws;

      ws.onopen = () => {
        ws!.send(JSON.stringify({ subscribe: asset, timeframe: tf }));
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string);
          if (msg.type !== 'candle') return;
          if (msg.symbol !== asset) return;
          const c = normalizeCandles([msg.candle]);
          if (c.length > 0) appendLiveCandle(asset, tf, c[0]);
        } catch (_) {}
      };

      ws.onclose = () => {
        liveWs = null;
        if (livePollActive) {
          reconnectTimeout = setTimeout(connect, 1000);
          liveWsReconnectTimer = reconnectTimeout;
        }
      };

      ws.onerror = () => { ws?.close(); };
    }

    connect();
    return () => disconnectLiveStream();
  }

  // ── HTTP polling for higher timeframes ────────────────────────────────
  // Poll interval: half the TF duration, minimum 500ms, max 5s (nominal TF lengths: a month = 30 days
  // only scales the poll rate, never a bar's close)
  const tfMs = (() => {
    const map: Record<string, number> = { '1m': 60_000, '2m': 120_000, '3m': 180_000, '5m': 300_000, '15m': 900_000, '30m': 1_800_000, '1h': 3_600_000, '4h': 14_400_000, '1D': 86_400_000, '1W': 604_800_000, '1Mo': 2_592_000_000 };
    return map[tf] ?? 60_000;
  })();
  const pollMs = Math.min(5_000, Math.max(500, tfMs * 0.005)); // 0.5% of TF, [500ms, 5s]

  async function pollLiveTicks() {
    if (!livePollActive) return;
    // Breaker open → skip this poll and check back after the cooldown window.
    if (circuitOpen()) {
      if (livePollActive) livePollTimerTick = setTimeout(pollLiveTicks, Math.max(pollMs, 5_000));
      return;
    }
    try {
      const url = `${CANDLE_CONSTRUCTOR_URL}/api/v2/candles/${asset}/live?timeframe=${tf}&count=5`;
      const res = await fetch(url, { signal: AbortSignal.timeout(4000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      recordFetchOk();
      const json = await res.json();
      const liveCandles = normalizeCandles(json.candles || []);
      if (liveCandles.length > 0) {
        const cached = getCandlesInstant(asset, tf);
        const lastCachedTime = cached.length > 0 ? cached[cached.length - 1].time : 0;
        const latestLive = liveCandles[liveCandles.length - 1];
        if (latestLive.time === lastCachedTime) {
          appendLiveCandle(asset, tf, latestLive);
        } else {
          for (const c of liveCandles) {
            if (c.time > lastCachedTime) appendLiveCandle(asset, tf, c);
          }
        }
      }
    } catch (_) { recordFetchFail(); }
    // When the breaker is open, slow the cadence so we don't busy-retry a down service.
    const nextMs = circuitOpen() ? Math.max(pollMs, 5_000) : pollMs;
    if (livePollActive) livePollTimerTick = setTimeout(pollLiveTicks, nextMs);
  }

  async function pollHistorical() {
    if (!livePollActive) return;
    try {
      if (agg) {
        const srcUrl = `${CANDLE_CONSTRUCTOR_URL}/api/v2/candles/${asset}/latest?timeframe=${agg.source}&count=30`;
        const res = await fetch(srcUrl, { signal: AbortSignal.timeout(5000) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const srcCandles = normalizeCandles(json.candles || json);
        const aggCandles = aggregateCandles(srcCandles, agg.intervalMs);
        if (aggCandles.length > 0) appendLiveCandle(asset, tf, aggCandles[aggCandles.length - 1]);
      } else {
        const url = `${CANDLE_CONSTRUCTOR_URL}/api/v2/candles/${asset}/latest?timeframe=${tf}&count=2`;
        const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const latest = normalizeCandles(json.candles || json);
        if (latest.length > 0) appendLiveCandle(asset, tf, latest[latest.length - 1]);
      }
    } catch (_) {}
    if (livePollActive) livePollTimer = setTimeout(pollHistorical, 30_000);
  }

  // Start immediately — no artificial delay
  livePollTimerTick = setTimeout(pollLiveTicks, 0);
  livePollTimer = setTimeout(pollHistorical, 30_000);
  return () => disconnectLiveStream();
}

export function disconnectLiveStream() {
  livePollActive = false;
  if (livePollTimer) { clearTimeout(livePollTimer); livePollTimer = null; }
  if (livePollTimerTick) { clearTimeout(livePollTimerTick); livePollTimerTick = null; }
  // Legacy WS cleanup (kept for safety)
  if (liveWsReconnectTimer) { clearTimeout(liveWsReconnectTimer); liveWsReconnectTimer = null; }
  if (liveWs) { liveWs.onclose = null; liveWs.close(); liveWs = null; }
}

/** Append a live candle to the cache (update last or add new) */
function appendLiveCandle(asset: string, tf: string, candle: CachedCandle) {
  const key = cacheKey(asset, tf);
  const entry = cache.get(key);
  if (!entry) return;

  const last = entry.data[entry.data.length - 1];
  if (last && last.time === candle.time) {
    // Update existing candle (tick update within same period)
    entry.data[entry.data.length - 1] = candle;
  } else if (!last || candle.time > last.time) {
    // New candle period — only push if strictly newer than last cached
    entry.data.push(candle);
  } else {
    // Stale candle older than most recent cached entry (e.g. historical poll
    // returning Oct data after live May candles are in cache) — discard silently
    return;
  }
  entry.timestamp = Date.now();
  notifyListeners(key, entry.data);
}

/**
 * Get current cache stats for debugging
 */
export function getCacheStats() {
  const stats: Record<string, { count: number; age: number; exhausted: boolean }> = {};
  for (const [key, entry] of cache.entries()) {
    stats[key] = {
      count: entry.data.length,
      age: Math.round((Date.now() - entry.timestamp) / 1000),
      exhausted: entry.historyExhausted,
    };
  }
  return stats;
}

// ── Auto-preload on first import ────────────────────────────────────
if (!preloadStarted) {
  preloadStarted = true;
  setTimeout(() => {
    preloadAll();
    startBackgroundRefresh();
    // Kick off gap-fill for the default asset on startup
    _triggerBackfillIfNeeded(DEFAULT_ASSET);
  }, 100);
}
